// Shared pieces for the planner pages: household context, notifications, confirm dialog,
// page header, loading/error states, member avatars and the catalog item picker.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack, TextField, Typography, createFilterOptions,
} from '@mui/material';
import { SxProps, Theme, alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';
import * as api from './plannerApi';
import * as T from './types';
import { avatarColor, initials, searchKey, sectionText } from './labels';

// ---------- Household ----------

interface HouseholdContextValue {
  household: T.Household | null;
  members: T.HouseholdMember[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Replaces the household after a change that returns it. */
  set: (household: T.Household | null) => void;
}

const HouseholdContext = createContext<HouseholdContextValue | undefined>(undefined);

export function HouseholdProvider({ children }: { children: React.ReactNode }) {
  const [household, setHousehold] = useState<T.Household | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      setHousehold(await api.getHousehold());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => ({
    household, members: household?.members ?? [], loading, error, reload, set: setHousehold,
  }), [household, loading, error, reload]);

  return <HouseholdContext.Provider value={value}>{children}</HouseholdContext.Provider>;
}

export function useHousehold(): HouseholdContextValue {
  const value = useContext(HouseholdContext);
  if (!value) throw new Error('useHousehold must be used inside HouseholdProvider');
  return value;
}

// ---------- Notifications (snackbar with an optional "Desfazer") ----------

interface Notice { message: string; actionText?: string; action?: () => void; severity?: 'success' | 'error' | 'info' | 'warning'; key: number; }
type Notify = (message: string, options?: { actionText?: string; action?: () => void; severity?: Notice['severity'] }) => void;

const NotifyContext = createContext<Notify>(() => {});

export function NotifyProvider({ children }: { children: React.ReactNode }) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const notify = useCallback<Notify>((message, options) => setNotice({ message, ...options, key: Date.now() }), []);
  const close = () => setNotice(null);
  return (
    <NotifyContext.Provider value={notify}>
      {children}
      <Snackbar
        key={notice?.key}
        open={notice !== null}
        autoHideDuration={notice?.action ? 6000 : 4000}
        onClose={(_, reason) => reason !== 'clickaway' && close()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 72, md: 24 }, left: { md: 'calc(50% + 128px)' } }}
      >
        <Alert
          severity={notice?.severity ?? 'info'}
          variant="filled"
          onClose={close}
          sx={{ whiteSpace: 'pre-line' }}
          action={notice?.action ? (
            <Button color="inherit" size="small" onClick={() => { notice.action!(); close(); }}>{notice.actionText ?? 'Desfazer'}</Button>
          ) : undefined}
        >
          {notice?.message}
        </Alert>
      </Snackbar>
    </NotifyContext.Provider>
  );
}

export const useNotify = () => useContext(NotifyContext);

/** Runs an API call and shows its error in the snackbar; returns undefined when it failed. */
export function useRun() {
  const notify = useNotify();
  return useCallback(async <R,>(work: () => Promise<R>, success?: string): Promise<R | undefined> => {
    try {
      const result = await work();
      if (success) notify(success, { severity: 'success' });
      return result;
    } catch (e: any) {
      notify(e.message ?? 'Ocorreu um erro.', { severity: 'error' });
      return undefined;
    }
  }, [notify]);
}

/**
 * Shows `message` with "Desfazer": the click runs `undo` like `useRun` (its error in the snackbar), and `after` gets its
 * answer only when the undo went through. `success` is said once undone; `severity` colours the message.
 */
export function useUndoable() {
  const notify = useNotify();
  const run = useRun();
  return useCallback(<R,>(message: string, undo: () => Promise<R>, after?: (result: R) => unknown,
    options?: { severity?: Notice['severity']; success?: string }) => {
    notify(message, {
      severity: options?.severity,
      actionText: 'Desfazer',
      action: async () => {
        const result = await run(undo, options?.success);
        if (result !== undefined) await after?.(result);
      },
    });
  }, [notify, run]);
}

// ---------- Loading data ----------

/**
 * Runs `load` on mount and when deps change, and hands the result to `apply` (an editor filling its fields).
 * Only the latest request counts: an older one that answers later, or one that answers after unmount, is dropped,
 * so a slow response can never replace a newer one. `load` null (nothing to load yet, a closed dialog) loads nothing.
 */
export function useLoadInto<R>(load: (() => Promise<R>) | null, apply: (result: R) => void, deps: React.DependencyList = []) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(load !== null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const latest = useRef(0);

  const reload = useCallback(async () => {
    const request = ++latest.current;
    const current = loadRef.current;
    setError(null);
    if (!current) { setLoading(false); return; }
    setLoading(true);
    try {
      const result = await current();
      if (request === latest.current) applyRef.current(result);
    } catch (e: any) {
      if (request === latest.current) setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      if (request === latest.current) setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, deps);
  useEffect(() => () => { latest.current++; }, []);

  return { error, setError, loading, reload };
}

/** Loads data on mount (and when deps change); `reload` refetches without clearing what is shown. Only the latest response is kept. */
export function useLoad<R>(load: () => Promise<R>, deps: React.DependencyList = []) {
  const [data, setData] = useState<R | undefined>(undefined);
  const { error, loading, reload } = useLoadInto(load, result => setData(() => result), deps);
  return { data, setData, error, loading, reload };
}

/** Spinner while loading the first time, error with "Tentar de novo", else the content. */
export function Loader({ loading, error, onRetry, hasData, children }: {
  loading: boolean; error: string | null; onRetry?: () => void; hasData: boolean; children: React.ReactNode;
}) {
  if (error && !hasData) {
    return (
      <Alert severity="error" action={onRetry && <Button color="inherit" size="small" onClick={onRetry}>Tentar de novo</Button>}>
        {error}
      </Alert>
    );
  }
  if (loading && !hasData) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  return <>{children}</>;
}

// ---------- Layout bits ----------

export function PageHeader({ title, subtitle, actions, eyebrow }: {
  title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode; eyebrow?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.25, sm: 2 }, mb: { xs: 2, md: 3 } }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {eyebrow && <Typography variant="overline" color="primary" component="div" sx={{ lineHeight: 1.6 }}>{eyebrow}</Typography>}
        <Typography variant="h5" component="h1" sx={{ fontSize: { xs: 24, md: 28 } }}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {actions && <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}>{actions}</Stack>}
    </Box>
  );
}

export function EmptyState({ title, text, action, icon }: { title: string; text?: string; action?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <Box sx={{ textAlign: 'center', py: { xs: 5, md: 7 }, px: 2, color: 'text.secondary' }}>
      {icon && (
        <Box sx={{ width: 56, height: 56, mx: 'auto', mb: 1.5, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: t => alpha(t.palette.primary.main, 0.09), color: 'primary.main', '& svg': { fontSize: 28 } }}>
          {icon}
        </Box>
      )}
      <Typography variant="subtitle1" sx={{ fontWeight: 650, color: 'text.primary' }}>{title}</Typography>
      {text && <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 520, mx: 'auto' }}>{text}</Typography>}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
}

export function SectionTitle({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <Typography variant="overline" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mt: 2.5, mb: 0.75 }}>
      {children}
      {count !== undefined && (
        <Box component="span" sx={{ px: 0.75, borderRadius: 1, bgcolor: 'action.selected', color: 'primary.dark', fontSize: 11, lineHeight: '18px' }}>{count}</Box>
      )}
    </Typography>
  );
}

/** Cards in as many columns as fit (one on a phone). */
export function CardGrid({ min = 280, gap = 1.5, children, sx }: { min?: number; gap?: number; children: React.ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box sx={[
      { display: 'grid', gap, alignItems: 'start', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `repeat(auto-fill, minmax(${min}px, 1fr))` } },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
      {children}
    </Box>
  );
}

/** Groups of different heights flowing in columns (shopping sections, places at home). Children keep together. */
export function Columns({ children, max = 3 }: { children: React.ReactNode; max?: 2 | 3 }) {
  return (
    <Box sx={{ columnCount: { xs: 1, md: 2, xl: max }, columnGap: 20, '& > *': { breakInside: 'avoid', display: 'inline-block', width: '100%' } }}>
      {children}
    </Box>
  );
}

/** A white panel with a small header, for side columns and grouped lists. */
export function Panel({ title, action, children, tone, sx }: {
  title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; tone?: 'warning'; sx?: SxProps<Theme>;
}) {
  return (
    <Paper variant="outlined" sx={[
      { overflow: 'hidden' },
      tone === 'warning' ? { borderColor: t => alpha(t.palette.warning.main, 0.35), bgcolor: t => alpha(t.palette.warning.main, 0.05) } : {},
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography sx={{ fontWeight: 650, fontSize: 15, flexGrow: 1, color: tone === 'warning' ? 'warning.dark' : undefined }}>{title}</Typography>
          {action}
        </Box>
      )}
      {children}
    </Paper>
  );
}

/** ‹ Semana de 13 a 19 out › with "Esta semana" when another week is shown. */
export function WeekNavigator({ title, isCurrent, onPrevious, onNext, onToday }: {
  title: string; isCurrent: boolean; onPrevious: () => void; onNext: () => void; onToday: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ display: 'inline-flex', alignItems: 'center', borderRadius: 3, p: 0.25 }}>
      <IconButton size="small" onClick={onPrevious} aria-label="Semana anterior"><ChevronLeftIcon /></IconButton>
      <Typography sx={{ fontWeight: 650, px: 1, minWidth: 150, textAlign: 'center', fontSize: 15 }}>{title}</Typography>
      <IconButton size="small" onClick={onNext} aria-label="Semana seguinte"><ChevronRightIcon /></IconButton>
      {!isCurrent && <Button size="small" onClick={onToday} sx={{ ml: 0.5 }}>Esta semana</Button>}
    </Paper>
  );
}

/** Shown on planner pages when the user has no household yet. */
export function NeedsHousehold({ children }: { children: React.ReactNode }) {
  const { household, loading, error, reload } = useHousehold();
  if (loading || error) return <Loader loading={loading} error={error} onRetry={reload} hasData={false}>{null}</Loader>;
  if (!household) {
    return (
      <EmptyState
        title="Ainda não tens um agregado"
        text="Cria o agregado familiar, ou aceita o convite de outra casa, para planear refeições, compras e tarefas."
        action={<Button variant="contained" component={RouterLink} to="/casa/agregado">Criar ou aceitar convite</Button>}
      />
    );
  }
  return <>{children}</>;
}

// ---------- Members ----------

/** The photo behind a versioned URL, as an object URL once loaded; null until then or without one. */
function usePhoto(photoUrl?: string | null): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    if (photoUrl) api.getPhotoObjectUrl(photoUrl).then(url => { if (!cancelled) setSrc(url); });
    return () => { cancelled = true; };
  }, [photoUrl]);
  return src;
}

/** A household member: the photo of their account when it has one, else their initials on their colour. */
export function MemberAvatar({ member, size = 32 }: {
  member: { name: string; color?: string | null; photoUrl?: string | null };
  size?: number;
}) {
  const src = usePhoto(member.photoUrl);
  return (
    <Avatar src={src ?? undefined} alt={member.name} sx={{ width: size, height: size, fontSize: size * 0.42, bgcolor: avatarColor(member) }}>
      {initials(member.name)}
    </Avatar>
  );
}

/** The account's photo when it has one, else its initials on the primary colour (as the Android UserAvatar). */
export function UserAvatar({ name, photoUrl, size = 32 }: { name: string; photoUrl?: string | null; size?: number }) {
  const src = usePhoto(photoUrl);
  return (
    <Avatar src={src ?? undefined} alt={name} sx={{ width: size, height: size, fontSize: size * 0.42, fontWeight: 600, bgcolor: 'primary.main' }}>
      {initials(name)}
    </Avatar>
  );
}

// ---------- Confirm dialog ----------

interface ConfirmOptions { title: string; text?: string; confirmText?: string; cancelText?: string; danger?: boolean; }

/** `const [confirm, dialog] = useConfirm();` then `if (await confirm({...}))` and render `{dialog}`. */
export function useConfirm(): [(options: ConfirmOptions) => Promise<boolean>, React.ReactElement] {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>(resolve => setState({ ...options, resolve })), []);
  const close = (ok: boolean) => { state?.resolve(ok); setState(null); };
  const dialog = (
    <Dialog open={state !== null} onClose={() => close(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{state?.title}</DialogTitle>
      {state?.text && <DialogContent><DialogContentText sx={{ whiteSpace: 'pre-line' }}>{state.text}</DialogContentText></DialogContent>}
      <DialogActions>
        <Button onClick={() => close(false)}>{state?.cancelText ?? 'Cancelar'}</Button>
        <Button variant="contained" color={state?.danger ? 'error' : 'primary'} onClick={() => close(true)}>
          {state?.confirmText ?? 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
  return [confirm, dialog];
}

// ---------- Catalog item picker ----------

/** An existing catalog item, or a new name to create. */
export type ItemChoice = { item: T.CatalogItem } | { newName: string };

type PickerOption = T.CatalogItem | { publicId: ''; newName: string };
const filter = createFilterOptions<PickerOption>({
  stringify: o => searchKey('newName' in o ? o.newName : `${o.name} ${o.aliases.join(' ')}`),
});

/**
 * Autocomplete over the catalog (name and aliases, accent-insensitive) that also offers
 * "Criar «texto»" for a name that is not there yet. Pass `items` to reuse a loaded catalog.
 */
export function ItemPicker({ value, onChange, items, kind, label = 'Artigo', autoFocus, allowNew = true, size }: {
  value: ItemChoice | null;
  onChange: (choice: ItemChoice | null) => void;
  items?: T.CatalogItem[];
  kind?: T.CatalogItemKind;
  label?: string;
  autoFocus?: boolean;
  allowNew?: boolean;
  size?: 'small' | 'medium';
}) {
  const [loaded, setLoaded] = useState<T.CatalogItem[] | null>(items ?? null);
  useEffect(() => {
    if (items) { setLoaded(items); return; }
    let alive = true;
    api.getCatalogItems(kind).then(r => alive && setLoaded(r)).catch(() => alive && setLoaded([]));
    return () => { alive = false; };
  }, [items, kind]);

  const selected: PickerOption | null = value === null ? null : 'item' in value ? value.item : { publicId: '', newName: value.newName };

  return (
    <Autocomplete<PickerOption, false, false, false>
      options={loaded ?? []}
      loading={loaded === null}
      value={selected}
      size={size}
      onChange={(_, option) => {
        if (!option) onChange(null);
        else if ('newName' in option) onChange({ newName: option.newName });
        else onChange({ item: option });
      }}
      filterOptions={(options, state) => {
        const filtered = filter(options, state);
        const text = state.inputValue.trim();
        const exists = options.some(o => !('newName' in o) && searchKey(o.name) === searchKey(text));
        if (allowNew && text.length > 0 && !exists) filtered.push({ publicId: '', newName: text });
        return filtered;
      }}
      getOptionLabel={o => ('newName' in o ? o.newName : o.name)}
      isOptionEqualToValue={(a, b) => ('newName' in a ? 'newName' in b && a.newName === b.newName : !('newName' in b) && a.publicId === b.publicId)}
      renderOption={(props, o) => {
        const { key, ...rest } = props as any;
        return (
          <li key={'newName' in o ? `new-${o.newName}` : o.publicId} {...rest}>
            {'newName' in o
              ? <Typography color="primary">Criar «{o.newName}»</Typography>
              : <Box>
                  <Typography variant="body2">{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{sectionText(o.storeSection)}</Typography>
                </Box>}
          </li>
        );
      }}
      renderInput={params => <TextField {...params} label={label} autoFocus={autoFocus} />}
    />
  );
}
